import React, { FC, useMemo } from 'react';
import {
	KompisText,
	KompisMiniPrice,
	KompisMediaCarousel,
} from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { IDexfSettings, IShoppingProduct, ITranslations } from '@inter-ikea-kompis/types';
import SkapaButton from '@ingka/button';

import SkapaModal, {
	Sheets as SkapaSheets,
	ModalHeader as SkapaModalHeader,
	ModalBody as SkapaModalBody,
	ModalFooter as SkapaModalFooter,
} from '@ingka/modal';

import {
	Platform,
	DeviceType,
	Orientation,
	CoverItem,
	ImageItem,
	SofaRangeName,
	WriteDirection,
} from 'sofa-reborn-react';
import CoverPickerDescription from './CoverProductDescription';
import CoverDisclaimer from './CoverDisclaimer';
import { localeService } from '../../services';
import {
	StyledSpanWrapper,
	EmptySpanWrapper,
	StyledSpan,
	CoversWrapper,
	StyledCover,
	StyledNameIcon,
	MissingCoverWrapper,
	SheetContainer,
	DescriptionContainer,
	CoverDisclaimerWrapper,
	CarouselContainer,
	StyledCoverHeader,
} from './CoverPickerStyle';

type Props = {
	platform: Platform;
	deviceType: DeviceType;
	orientation: Orientation;
	writeDirection: WriteDirection;
	images: ImageItem[];
	covers: CoverItem[];
	nrOfEmptyElements: number;
	replaceCovers: CoverItem[];
	selectedCover: CoverItem;
	selectedIndex?: number;
	sofaRangeName: SofaRangeName;
	missingCover: boolean;
	showSheet: boolean;
	kompisSettings?: IDexfSettings | null;
	kompisTranslations?: ITranslations | null;
	isAdditionalCover?: boolean;
	additionalCoverItemsProducts?: IShoppingProduct[];
	stockedIkeaPlus: boolean;
	onSkipListing?: () => void;
	onClose?: () => void;
	onCoverClick?: (coverItem: CoverItem) => void;
	onChange?: (selectedIndex: number) => void;
	onAddAdditionalCoverClick?: (coverItem: CoverItem) => void;
};

const CoverPicker: FC<Props> = (props) => {
	const { onClose, onChange, onCoverClick, onAddAdditionalCoverClick, onSkipListing } = props;
	const covers = props.missingCover ? props.replaceCovers : props.covers;

	const getHeaderTitle = (): string => {
		return props.missingCover
			? localeService.getTranslation('sprValidationCoverMissingTitle')
			: props.isAdditionalCover
			? localeService.getTranslation('additionalCoverSheetHeader')
			: localeService.getTranslation('selectCover');
	};

	const cover = useMemo(() => {
		const currentCover = covers.find((cover) => cover.coverName === props.selectedCover.coverName);
		return {
			coverName: currentCover ? currentCover.name : '',
		};
	}, [covers, props.selectedCover]);

	const coverElements = covers.map(function (cover) {
		const active = cover.articleId === props.selectedCover?.articleId ? 'active' : '';

		return (
			<StyledSpanWrapper
				platform={props.platform}
				key={cover.articleId}
				onClick={(): void => onCoverClick && onCoverClick(cover)}
				onKeyDown={(e): void => {
					if (e.key === 'Enter') {
						onCoverClick && onCoverClick(cover);
					}
				}}
				role="button"
				aria-describedby={cover.articleId}
				tabIndex={0}
				style={{
					borderRadius: '50%',
				}}
			>
				<StyledSpan
					className={active}
					platform={props.platform}
					color={cover.color}
					borderColor={cover.borderColor as string}
				>
					<div id={cover.articleId} style={{ display: 'none' }}>
						{cover.name}
					</div>
				</StyledSpan>
			</StyledSpanWrapper>
		);
	});

	/* Create empty wrapper to left align dots */
	for (let i = 0; i < props.nrOfEmptyElements; i++) {
		coverElements.push(<EmptySpanWrapper key={i} platform={props.platform}></EmptySpanWrapper>);
	}

	const sheetFooter = props.isAdditionalCover ? (
		<SkapaModalFooter>
			<SkapaButton
				text={localeService.getTranslation('additionalCoverSheetButton')}
				type={'primary'}
				onClick={(): void =>
					onAddAdditionalCoverClick && onAddAdditionalCoverClick(props.selectedCover)
				}
				style={{ width: '100%' }}
			></SkapaButton>
		</SkapaModalFooter>
	) : null;

	return (
		<SkapaModal visible={props.showSheet} handleCloseBtn={(): void => onClose && onClose()}>
			<SkapaSheets
				size={'small'}
				alignment={'right'}
				header={<SkapaModalHeader title={getHeaderTitle()} />}
				footer={sheetFooter}
				style={{
					zIndex: 10,
					borderTop:
						SkapaTheme.border.thickness.thin + 'px solid' + SkapaTheme.color.border.default,
					borderBottom:
						SkapaTheme.border.thickness.thin + 'px solid' + SkapaTheme.color.border.default,
				}}
			>
				<SkapaModalBody style={{ paddingInline: '1.5rem' }}>
					<SheetContainer>
						{props.isAdditionalCover ? (
							<KompisText theme={SkapaTheme}>
								{localeService.getTranslation('additionalCoverCardText')}
							</KompisText>
						) : null}
						<div>
							{props.missingCover ? (
								<MissingCoverWrapper>
									<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyMedium}>
										{localeService.getTranslation('sprValidationCoverMissingDescription')}
									</KompisText>
								</MissingCoverWrapper>
							) : null}
							<CoversWrapper missingCover={props.missingCover}>
								<SkapaButton
									text={localeService.getTranslation('skipCoverSelections')}
									type={'primary'}
									inverseTheme
									onClick={(): void => onSkipListing && onSkipListing()}
									className={'skipButton'}
								/>
								<StyledCover>{coverElements}</StyledCover>
							</CoversWrapper>
						</div>
						<DescriptionContainer
							platform={props.platform}
							deviceType={props.deviceType}
							writeDirection={props.writeDirection}
							id="description-container"
						>
							<StyledCoverHeader>
								<StyledNameIcon>
									<KompisText
										theme={SkapaTheme}
										type={ThemeFontStyleTypeEnum.bodyLarge}
										bold={true}
									>
										{props.sofaRangeName}
									</KompisText>
									<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyLarge}>
										{cover.coverName}
									</KompisText>
								</StyledNameIcon>
								{props.kompisTranslations &&
								props.kompisSettings &&
								props.isAdditionalCover &&
								props.additionalCoverItemsProducts &&
								props.additionalCoverItemsProducts.length > 0 ? (
									<KompisMiniPrice
										shoppingProducts={props.additionalCoverItemsProducts}
										settings={props.kompisSettings}
										translations={props.kompisTranslations.kompis}
										theme={SkapaTheme}
										// @ts-expect-error Kompis elemenets support style but it isn't exposed
										style={{ alignSelf: 'center' }}
									/>
								) : null}
							</StyledCoverHeader>
							{localeService.getApplicationSetting('showCoverDisclaimer') &&
							props.selectedCover.isIkeaPlus &&
							!props.stockedIkeaPlus ? (
								<CoverDisclaimerWrapper>
									<CoverDisclaimer
										writeDirection={props.writeDirection}
										title={localeService.getTranslation('coverDisclaimerTitle')}
										text={localeService.getTranslation('coverDisclaimerInfo')}
									/>
								</CoverDisclaimerWrapper>
							) : null}
							{props.kompisSettings && props.kompisTranslations && (
								<CarouselContainer>
									<KompisMediaCarousel
										translations={props.kompisTranslations.kompis}
										theme={SkapaTheme}
										mediaItems={props.images}
										skipListingLabel={localeService.getTranslation('skipImageThumbnailList')}
										showLessLabel={localeService.getTranslation('carouselShowLess')}
										selectedIndex={props.selectedIndex as number | 0}
										showArrows={props.images.length > 1}
										showScrollbar={props.images.length > 1}
										onChange={(e: CustomEvent): void =>
											onChange && onChange(e.detail.selectedIndex)
										}
										// @ts-expect-error Kompis elemenets support style but it isn't exposed
										style={{ width: '75%', height: '100%', margin: '0 auto' }}
									/>
								</CarouselContainer>
							)}
							<CoverPickerDescription />
						</DescriptionContainer>
					</SheetContainer>
				</SkapaModalBody>
			</SkapaSheets>
		</SkapaModal>
	);
};
export default CoverPicker;
